import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { queryKeys, XLSX_ACEPT, XLSX_FORMAT } from '@/constant/common'
import { OPERATIONS_ROUTES } from '@/constant/route'
import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import { NotifyService } from '@/helpers'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { saveAs } from 'file-saver'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useSetUpUI } from '../../context'
import { useMk2DecoderUploadV2 } from '../../hooks/setup/useMk2DecoderUploadV2'
import useSetupUrlInterfaceId from '../../hooks/setup/useSetupUrlInterfaceId'
import SelectExcelFile from './SelectExcelFile'
import classes from './styles.module.scss'

type UploadStep = 'SELECT_FILE' | 'UPLOADING' | 'COMPLETED' | 'FAILED'

const Mk2DecoderModalV2 = () => {
  const { closeModal } = useSetUpUI()
  // SCRUM-976: pin to URL :id, not the global selected interface (AuthContext/localStorage).
  // This modal is rendered as a sibling of <Routes> (via ModalUI), not as a descendant of
  // the matched discovery/:id route, so useParams() here would always be undefined.
  const routeId = useSetupUrlInterfaceId()
  const interfaceId = Number(routeId)
  const themeMode = useSelector(selectTheme)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isDarkMode = themeMode === mode_type.dark

  const { refetch: refetchInterfaceById } = useSearchInterfaceId(String(interfaceId))

  const [fileNames, setFileNames] = useState<Record<string, File>>({})
  const [filePath, setFilePath] = useState<string | null>(null)
  const [step, setStep] = useState<UploadStep>('SELECT_FILE')
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const uploadHook = useMk2DecoderUploadV2({
    interfaceId: Number(interfaceId),
    onSuccess: () => {
      setStep('COMPLETED')
      toast.success('Import MK2 Decoder completed successfully')
      setTimeout(() => {
        closeModal()
      }, 1500)
    },
    onError: (error) => {
      setStep('FAILED')
      NotifyService.error(error)
    },
    onCancel: async () => {
      // After cancel completes and no active task, check interface status
      const { data: interfaceData } = await refetchInterfaceById()

      if (interfaceData?.status === true) {
        // Interface is already setup, close modal and redirect to dashboard
        closeModal()
        navigate(OPERATIONS_ROUTES.index.to)
      } else {
        // Interface not setup, show file selection
        setStep('SELECT_FILE')
      }
      toast.success('Import cancelled')
    },
  })

  const handleStopClick = () => {
    setShowCancelConfirm(true)
  }

  const handleConfirmCancel = async () => {
    setShowCancelConfirm(false)
    await uploadHook.cancelTask()
  }

  const handleCloseCancelConfirm = () => {
    setShowCancelConfirm(false)
  }

  const onDownload = async () => {
    await controllerDataConfigApi
      .getMk2Template(Number(interfaceId))
      .then((res) => {
        saveAs(res.data, 'MK2 Decoder_Template.xlsx')
      })
  }

  useEffect(() => {
    const fileKey = Object.keys(fileNames)[0]
    if (fileKey) {
      setFilePath(fileNames[fileKey].name)
    } else {
      setFilePath(null)
    }
  }, [fileNames])

  // Check for active task on mount
  useEffect(() => {
    const checkForActiveTask = async () => {
      const activeTask = await uploadHook.checkActiveTask()
      if (activeTask?.hasActiveTask) {
        setStep('UPLOADING')
      }
    }
    checkForActiveTask()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Refresh decoders list when modal closes (unmount)
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries([queryKeys.controller])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChangeFile = async (file: File[]) => {
    const newFileNames = file.reduce((acc, f) => {
      acc[f.name] = f
      return acc
    }, {} as Record<string, File>)

    // Replace instead of merge to ensure new file is selected
    setFileNames(newFileNames)
  }

  const handleImport = async () => {
    const fileKey = Object.keys(fileNames)[0]
    if (!fileKey) return

    setStep('UPLOADING')
    await uploadHook.upload(fileNames[fileKey])
  }

  const handleClose = () => {
    if (step === 'UPLOADING') {
      return
    }
    closeModal()
  }

  const buttonStyle = !filePath
    ? {
        opacity: 0.5,
        pointerEvents: 'none' as const,
        userSelect: 'none' as const,
        backgroundColor: '#efefef',
        color: '#8a8a8a',
      }
    : {}

  // Calculate completed steps
  const completedSteps =
    uploadHook.progress?.steps?.filter(
      (s) => s.status === 2, // Completed
    ).length || 0
  const totalSteps = uploadHook.progress?.steps?.length || 1

  const renderContent = () => {
    if (step === 'UPLOADING') {
      return (
        <Stack spacing={2} width="100%">
          {/* Title - always show */}
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '14px',
              color: isDarkMode ? '#fff' : '#000',
            }}
          >
            Import MK2 Decoder
          </Typography>

          {uploadHook.overallProgress === 0 ? (
            <Stack alignItems="center" spacing={2} py={4}>
              <CircularProgress size={48} />
              <Typography
                sx={{
                  fontSize: '14px',
                  color: isDarkMode ? '#B0BEC5' : '#5c5c5c',
                }}
              >
                Uploading file...
              </Typography>
            </Stack>
          ) : (
            <Stack width="100%" spacing={2} py={2}>
              {/* Progress bar */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress
                      color="primary"
                      variant={
                        uploadHook.overallProgress > 0
                          ? 'determinate'
                          : 'indeterminate'
                      }
                      value={uploadHook.overallProgress}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 45 }}>
                    <Typography variant="body2" color="text.main">
                      {`${Math.round(uploadHook.overallProgress)}%`}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Description below progress bar */}
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '120%',
                  textAlign: 'center',
                  color: isDarkMode ? '#B0BEC5' : '#5c5c5c',
                }}
              >
                Please wait while importing.
              </Typography>

              {/* Stop button */}
              <Stack direction="row" justifyContent="center" mt={1}>
                <Button
                  variant="secondary"
                  onClick={handleStopClick}
                  disabled={uploadHook.isCancelling}
                >
                  {uploadHook.isCancelling ? 'Stopping...' : 'Stop'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      )
    }

    if (step === 'COMPLETED') {
      return (
        <Stack alignItems="center" py={4}>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#4caf50',
            }}
          >
            Import completed successfully!
          </Typography>
        </Stack>
      )
    }

    if (step === 'FAILED') {
      return (
        <Stack spacing={2}>
          <Typography className={classes.txtImportMk2}>
            Import MK2 Decoder
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#f44336',
            }}
          >
            {uploadHook.error || 'Import failed. Please try again.'}
          </Typography>
          <Button variant="secondary" sx={{ mt: '20px' }}>
            <SelectExcelFile
              accept={XLSX_ACEPT}
              formats={XLSX_FORMAT}
              onChange={onChangeFile}
              filePath={filePath}
            />
          </Button>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            mt="33px"
          >
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              sx={{ ...buttonStyle }}
              onClick={handleImport}
            >
              Try Again
            </Button>
          </Stack>
        </Stack>
      )
    }

    // SELECT_FILE step
    return (
      <>
        <Typography className={classes.txtImportMk2}>
          Import MK2 Decoder
        </Typography>
        <Typography mt="6px" className={classes.txtContentImport}>
          Import a list of mk2 decoders by providing the list of decoder number
          via an excel file
        </Typography>

        <Button variant="secondary" sx={{ mt: '20px' }}>
          <SelectExcelFile
            accept={XLSX_ACEPT}
            formats={XLSX_FORMAT}
            onChange={onChangeFile}
            filePath={filePath}
          />
        </Button>

        <Button
          className={classes.btnDownload}
          sx={{
            color: isDarkMode ? '#fff' : '#004744',
          }}
          onClick={onDownload}
        >
          Download excel template
        </Button>

        <Stack direction="row" spacing={1} justifyContent="flex-end" mt="33px">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            sx={{ ...buttonStyle }}
            onClick={handleImport}
          >
            Import
          </Button>
        </Stack>
      </>
    )
  }

  return (
    <>
      <Stack
        className={classes.rootMk2Modal}
        sx={{
          height: step === 'UPLOADING' || step === 'COMPLETED' ? 'auto' : '263px',
          minHeight:
            step === 'UPLOADING' || step === 'COMPLETED' ? '200px' : '263px',
        }}
      >
        {renderContent()}
      </Stack>

      {/* Cancel confirmation dialog */}
      <Dialog open={showCancelConfirm} onClose={handleCloseCancelConfirm}>
        <Box sx={{ backgroundColor: 'background.default' }}>
          <DialogTitle sx={{ padding: '41px 152px 4px 34px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '20px',
                lineHeight: '24px',
                minHeight: '24px',
                minWidth: '192px',
              }}
            >
              Cancel Import?
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ paddingLeft: '34px', paddingBottom: '18px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '1.3',
                maxWidth: '314px',
              }}
            >
              Are you sure you want to cancel the import? This action cannot be
              undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ paddingBottom: '32px', paddingRight: '30px' }}>
            <Button
              variant="secondary"
              sx={{ width: '99px' }}
              onClick={handleCloseCancelConfirm}
            >
              No
            </Button>
            <Button
              variant="primary"
              sx={{ width: '99px' }}
              onClick={handleConfirmCancel}
            >
              Yes
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}

export default Mk2DecoderModalV2
